import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import './Reports.css';

interface CustomPayout {
  id: number;
  affiliateId: number;
  affiliateName: string;
  offerId: number;
  offerName: string;
  advertiser: string;
  eventType: 'install' | 'lead' | 'sale' | 'event';
  defaultPayout: number;
  customPayout: number;
  country?: string;
  device?: string;
  startDate: string;
  endDate?: string;
  status: 'active' | 'paused' | 'expired';
  createdBy: string;
  createdDate: string;
  lastModified: string;
}

const CustomPayouts: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [affiliateFilter, setAffiliateFilter] = useState<string>('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [customPayouts, setCustomPayouts] = useState<CustomPayout[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCustomPayouts = async () => {
      try {
        setLoading(true);
        const response = await apiService.getCustomPayouts({
          status: statusFilter !== 'all' ? statusFilter : undefined
        });
        setCustomPayouts(response.data);
      } catch (error: any) {
        console.error('Failed to fetch custom payouts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomPayouts();
  }, [statusFilter]);

  const [oldCustomPayouts] = useState<CustomPayout[]>([
    {
      id: 1,
      affiliateId: 502,
      affiliateName: 'Marvel Ads',
      offerId: 1,
      offerName: 'TikTok App Install - iOS',
      advertiser: 'TikTok Inc',
      eventType: 'install',
      defaultPayout: 2.50,
      customPayout: 3.20,
      country: 'US',
      device: 'iOS',
      startDate: '2023-08-01',
      endDate: '2023-12-31',
      status: 'active',
      createdBy: 'Admin',
      createdDate: '2023-07-28',
      lastModified: '2023-08-15'
    },
    {
      id: 2,
      affiliateId: 503,
      affiliateName: 'Pytoo API',
      offerId: 2,
      offerName: 'Gaming App Install - Android',
      advertiser: 'GameStudio',
      eventType: 'install',
      defaultPayout: 1.80,
      customPayout: 2.40,
      startDate: '2023-08-15',
      status: 'active',
      createdBy: 'Manager1',
      createdDate: '2023-08-14',
      lastModified: '2023-08-20'
    },
    {
      id: 3,
      affiliateId: 505,
      affiliateName: 'CrowMobi Premium',
      offerId: 3,
      offerName: 'E-commerce Lead Gen',
      advertiser: 'ShopMart',
      eventType: 'lead',
      defaultPayout: 5.00,
      customPayout: 7.50,
      country: 'UK',
      startDate: '2023-07-01',
      endDate: '2023-08-31',
      status: 'expired',
      createdBy: 'Admin',
      createdDate: '2023-06-28',
      lastModified: '2023-07-15'
    },
    {
      id: 4,
      affiliateId: 502,
      affiliateName: 'Marvel Ads',
      offerId: 5,
      offerName: 'Subscription Service Sale',
      advertiser: 'StreamPro',
      eventType: 'sale',
      defaultPayout: 15.00,
      customPayout: 18.50,
      device: 'Desktop',
      startDate: '2023-08-20',
      status: 'paused',
      createdBy: 'Manager2',
      createdDate: '2023-08-18',
      lastModified: '2023-08-25'
    }
  ]);

  const [newPayout, setNewPayout] = useState({
    affiliateId: '',
    offerId: '',
    customPayout: '',
    country: '',
    device: '',
    startDate: '',
    endDate: ''
  });

  const filteredPayouts = customPayouts.filter(payout => {
    const searchMatch = payout.affiliateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       payout.offerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       payout.advertiser.toLowerCase().includes(searchTerm.toLowerCase());

    const statusMatch = statusFilter === 'all' || payout.status === statusFilter;
    const affiliateMatch = affiliateFilter === 'all' || payout.affiliateId.toString() === affiliateFilter;

    return searchMatch && statusMatch && affiliateMatch;
  });

  const calculateStats = () => {
    return {
      total: filteredPayouts.length,
      active: filteredPayouts.filter(p => p.status === 'active').length,
      paused: filteredPayouts.filter(p => p.status === 'paused').length,
      expired: filteredPayouts.filter(p => p.status === 'expired').length,
      avgIncrease: filteredPayouts.reduce((sum, p) => {
        const increase = ((p.customPayout - p.defaultPayout) / p.defaultPayout) * 100;
        return sum + increase;
      }, 0) / filteredPayouts.length
    };
  };

  const stats = calculateStats();

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'expired': return 'danger';
      default: return 'secondary';
    }
  };

  const getEventTypeClass = (eventType: string) => {
    switch (eventType) {
      case 'install': return 'install';
      case 'lead': return 'lead';
      case 'sale': return 'sale';
      case 'event': return 'event';
      default: return 'default';
    }
  };

  const handleAddPayout = () => {
    console.log('Adding new custom payout:', newPayout);
    setNewPayout({
      affiliateId: '',
      offerId: '',
      customPayout: '',
      country: '',
      device: '',
      startDate: '',
      endDate: ''
    });
    setShowAddForm(false);
  };

  const toggleStatus = (id: number) => {
    console.log(`Toggling status for payout ${id}`);
  };

  return (
    <div className="reports-page">
      <div className="page-header">
        <h1>Custom Payouts</h1>
        <div className="header-actions">
          <button
            className="btn btn-success"
            onClick={() => setShowAddForm(true)}
          >
            Add Custom Payout
          </button>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="report-summary">
        <div className="summary-card">
          <h3>Total Custom Payouts</h3>
          <div className="value">{stats.total}</div>
        </div>
        <div className="summary-card">
          <h3>Active</h3>
          <div className="value approved">{stats.active}</div>
        </div>
        <div className="summary-card">
          <h3>Paused</h3>
          <div className="value pending">{stats.paused}</div>
        </div>
        <div className="summary-card">
          <h3>Expired</h3>
          <div className="value rejected">{stats.expired}</div>
        </div>
        <div className="summary-card">
          <h3>Avg Payout Increase</h3>
          <div className="value">{stats.avgIncrease.toFixed(1)}%</div>
        </div>
      </div>

      {/* Add Form Modal */}
      {showAddForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#4a5568',
            padding: '30px',
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90vw'
          }}>
            <h3 style={{ color: '#ecf0f1', marginBottom: '20px' }}>Add Custom Payout</h3>
            <div style={{ display: 'grid', gap: '15px' }}>
              <div>
                <label style={{ color: '#a0aec0', fontSize: '14px', fontWeight: 600 }}>Affiliate ID:</label>
                <input
                  type="number"
                  value={newPayout.affiliateId}
                  onChange={(e) => setNewPayout({...newPayout, affiliateId: e.target.value})}
                  className="search-input"
                  style={{ width: '100%', marginTop: '5px' }}
                />
              </div>
              <div>
                <label style={{ color: '#a0aec0', fontSize: '14px', fontWeight: 600 }}>Offer ID:</label>
                <input
                  type="number"
                  value={newPayout.offerId}
                  onChange={(e) => setNewPayout({...newPayout, offerId: e.target.value})}
                  className="search-input"
                  style={{ width: '100%', marginTop: '5px' }}
                />
              </div>
              <div>
                <label style={{ color: '#a0aec0', fontSize: '14px', fontWeight: 600 }}>Custom Payout:</label>
                <input
                  type="number"
                  step="0.01"
                  value={newPayout.customPayout}
                  onChange={(e) => setNewPayout({...newPayout, customPayout: e.target.value})}
                  className="search-input"
                  style={{ width: '100%', marginTop: '5px' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ color: '#a0aec0', fontSize: '14px', fontWeight: 600 }}>Country (Optional):</label>
                  <input
                    type="text"
                    value={newPayout.country}
                    onChange={(e) => setNewPayout({...newPayout, country: e.target.value})}
                    className="search-input"
                    style={{ width: '100%', marginTop: '5px' }}
                  />
                </div>
                <div>
                  <label style={{ color: '#a0aec0', fontSize: '14px', fontWeight: 600 }}>Device (Optional):</label>
                  <input
                    type="text"
                    value={newPayout.device}
                    onChange={(e) => setNewPayout({...newPayout, device: e.target.value})}
                    className="search-input"
                    style={{ width: '100%', marginTop: '5px' }}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ color: '#a0aec0', fontSize: '14px', fontWeight: 600 }}>Start Date:</label>
                  <input
                    type="date"
                    value={newPayout.startDate}
                    onChange={(e) => setNewPayout({...newPayout, startDate: e.target.value})}
                    className="date-input"
                    style={{ width: '100%', marginTop: '5px' }}
                  />
                </div>
                <div>
                  <label style={{ color: '#a0aec0', fontSize: '14px', fontWeight: 600 }}>End Date (Optional):</label>
                  <input
                    type="date"
                    value={newPayout.endDate}
                    onChange={(e) => setNewPayout({...newPayout, endDate: e.target.value})}
                    className="date-input"
                    style={{ width: '100%', marginTop: '5px' }}
                  />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={handleAddPayout}
              >
                Add Payout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="report-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Affiliate:</label>
            <select
              value={affiliateFilter}
              onChange={(e) => setAffiliateFilter(e.target.value)}
              className="group-select"
            >
              <option value="all">All Affiliates</option>
              <option value="502">Marvel Ads (502)</option>
              <option value="503">Pytoo API (503)</option>
              <option value="505">CrowMobi Premium (505)</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search payouts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
      </div>

      {/* Payouts Table */}
      <div className="report-content">
        <div className="table-container">
          <table className="report-table">
            <thead>
              <tr>
                <th>Affiliate</th>
                <th>Offer</th>
                <th>Advertiser</th>
                <th>Event Type</th>
                <th>Default Payout</th>
                <th>Custom Payout</th>
                <th>Increase</th>
                <th>Targeting</th>
                <th>Period</th>
                <th>Status</th>
                <th>Created By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayouts.map((payout) => {
                const increase = ((payout.customPayout - payout.defaultPayout) / payout.defaultPayout) * 100;
                return (
                  <tr key={payout.id}>
                    <td>
                      <div className="affiliate-cell">
                        <span className="affiliate-name">{payout.affiliateName}</span>
                        <span className="affiliate-id">ID: {payout.affiliateId}</span>
                      </div>
                    </td>
                    <td>
                      <div className="offer-cell">
                        <span className="offer-name">{payout.offerName}</span>
                        <span className="offer-id">ID: {payout.offerId}</span>
                      </div>
                    </td>
                    <td>{payout.advertiser}</td>
                    <td>
                      <span className={`event-type ${getEventTypeClass(payout.eventType)}`}>
                        {payout.eventType.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span className="payout">${payout.defaultPayout.toFixed(2)}</span>
                    </td>
                    <td>
                      <span className="revenue">${payout.customPayout.toFixed(2)}</span>
                    </td>
                    <td>
                      <span className={increase > 0 ? 'profit' : 'rejected'}>
                        {increase > 0 ? '+' : ''}{increase.toFixed(1)}%
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                        {payout.country && <div>Country: {payout.country}</div>}
                        {payout.device && <div>Device: {payout.device}</div>}
                        {!payout.country && !payout.device && <div>No targeting</div>}
                      </div>
                    </td>
                    <td>
                      <div className="datetime-cell">
                        <span className="date">{payout.startDate}</span>
                        <span className="time">{payout.endDate || 'No end date'}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusClass(payout.status)}`}>
                        {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                        <div>{payout.createdBy}</div>
                        <div>{payout.createdDate}</div>
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-info btn-sm">Edit</button>
                        <button
                          className={`btn btn-sm ${payout.status === 'active' ? 'btn-warning' : 'btn-success'}`}
                          onClick={() => toggleStatus(payout.id)}
                        >
                          {payout.status === 'active' ? 'Pause' : 'Activate'}
                        </button>
                        <button className="btn btn-danger btn-sm">Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="table-footer">
        <div className="table-info">
          Showing {filteredPayouts.length} custom payouts
        </div>
      </div>
    </div>
  );
};

export default CustomPayouts;